import React, { Component } from 'react';
import { Helmet } from 'react-helmet-async';
import dayjs from 'dayjs';

import { handleError } from 'utils/common';

import ConnectContainer from 'containers/ConnectContainer';
import { UserAuthHOC, CandidateAuthHOC } from 'containers/HOC';

import ReadyContainer from 'connectors/ReadyContainer';

import { promisePickerOptions } from 'connectors/Defaults/promises';
import { promisePostCompanies } from 'pages/AdminPortal/ToolboxCandidateEditorPage/promises';

import Button from 'components/Button';
import CandidateImageUploader from 'components/CandidateImageUploader';
import Content from 'components/Content';
import Title from 'components/Title';

import WorkInputs from '../../CandidateEditorPages/EditWork/components/WorkInputs';
import WorkDisplay from '../../CandidateEditorPages/EditWork/components/WorkDisplay';

import duck from '../redux';
import MainContent from './components/MainContent';

import styles from './CommunityProfile.scss';

@UserAuthHOC()
@CandidateAuthHOC()
class CreateProfile extends Component {
  constructor(props) {
    super(props);

    const {
      user: {
        currentProfile: { workHistories = [], candidateEducations = [], recentGrad } = {},
      } = {},
    } = props;

    this.state = {
      workHistories,
      candidateEducations,
      origWorkHistories: workHistories,
      recentGrad,
      reorderAttempts: 0,
      pickerOptions: {},
    };
  }

  componentDidMount() {
    const {
      actions: { serverErrorSet },
    } = this.props;

    serverErrorSet({ errors: [] });
    promisePickerOptions()
      .then((pickerOptions) =>
        this.setState({
          pickerOptions,
        })
      )
      .catch(handleError);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { user: { currentProfile: { workHistories = [], candidateEducations = [] } = {} } = {} } =
      nextProps;

    this.setState({
      workHistories,
      candidateEducations,
      origWorkHistories: workHistories,
    });

    const firstMovedWHIdx = workHistories.findIndex((wh) => wh.moved);
    if (firstMovedWHIdx >= 0) {
      if (this[`workHistory${firstMovedWHIdx}`]) {
        this[`workHistory${firstMovedWHIdx}`].scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
      }
      setTimeout(() => {
        const { workHistories } = this.state;
        const newWorkHistories = workHistories.map((wh) => ({
          ...wh,
          moved: false,
        }));

        this.setState({
          workHistories: newWorkHistories,
          origWorkHistories: newWorkHistories,
        });
      }, 500);
    }
  }

  componentWillUnmount() {
    this.setState({
      workHistories: [],
      candidateEducations: [],
      origWorkHistories: [],
    });
  }

  setWHRef = (ref, idx) => {
    this[`workHistory${idx}`] = ref;
  };

  handleAddWorkHistory = () => {
    const {
      user: { currentProfile: { candidateRequirement: { roles = [] } = {} } = {} } = {},
      recentRole,
    } = this.props;

    const desiredRole = roles && roles[0] ? roles[0] : 'Sales Executive (AE)';

    const { workHistories } = this.state;

    const newWorkHistories = workHistories.slice();

    const newWorkHistory = {
      startMonth: '',
      startYear: '',
      endMonth: '',
      endYear: '',
      startDate: '',
      endDate: '',
      quotasHit: '',
      stackRank: '',
      soldToCompanySizes: [],
      soldToCompanies: [],
      soldToDepartments: [],
      soldToIndustries: [],
      territories: [],
      productTypes: [],
      awards: [],
      inboundOutbound: '',
      saleCycleTime: '',
      salesPeriods: [],
      description: '',
      company: {
        id: '',
        picture: {
          original: '',
          thumb: '',
          small: '',
          medium: '',
          large: '',
          xlarge: '',
          xxlarge: '',
        },
      },
      role: null,
      position: '',
      isEditing: true,
    };

    if (workHistories.length === 0) {
      newWorkHistory.role = recentRole || desiredRole;
    }

    newWorkHistories.push(newWorkHistory);

    this.setState({
      workHistories: newWorkHistories,
    });
  };

  handleCancelEditingWork = (idx) => {
    const { workHistories, origWorkHistories } = this.state;

    const origWorkHistory = origWorkHistories[idx];

    const newWorkHistories = workHistories.slice();

    if (origWorkHistory && origWorkHistory.id) {
      newWorkHistories.splice(idx, 1, { ...origWorkHistory, isEditing: false });
      this.setState({
        workHistories: newWorkHistories,
      });
    } else {
      this.handleWorkHistoryDelete({ idx });
    }
  };

  handleReorderWorkHistories = ({ idx }) => {
    const {
      actions: { reorderWorkStarted },
    } = this.props;

    const {
      isPostingCompany,
      reorderAttempts,
      workHistories: origWorkHistories,
      candidateEducations: origCandidateEducations,
    } = this.state;

    if (isPostingCompany && reorderAttempts < 4) {
      setTimeout(() => {
        this.handleReorderWorkHistories({ idx });
        this.setState({ reorderAttempts: reorderAttempts + 1 });
      }, 500);
    } else {
      reorderWorkStarted({
        origWorkHistories,
        origCandidateEducations,
        idx,
      });
      this.setState({ reorderAttempts: 0 });
    }
  };

  handleToggleEditingWork = (idx) => {
    const { workHistories } = this.state;

    if (workHistories[idx].isEditing) {
      setTimeout(() => this.handleReorderWorkHistories({ idx }), 250);
    } else {
      const newWorkHistories = workHistories.map((wh, index) => {
        if (index === idx) {
          return {
            ...wh,
            isEditing: true,
          };
        }
        return wh;
      });

      const origWorkHistory = workHistories[idx];
      const newOrigWorkHistories = workHistories.slice();
      newOrigWorkHistories.splice(idx, 1, origWorkHistory);

      this.setState({
        workHistories: newWorkHistories,
        origWorkHistories: newOrigWorkHistories,
      });
    }
  };

  makeWorkHistoryInputs = (workHistory, idx) => {
    const { pickerOptions = {} } = this.state;
    const { dispatch } = this.props;

    pickerOptions.role = pickerOptions.role
      ? pickerOptions.role.filter((el) => !['Non-Sales', 'B2C / Retail'].includes(el))
      : [];

    const { isEditing } = workHistory;

    const workInputsProps = {
      dispatch,
      workInputsType: 'limited',
      workHistory,
      idx,
      hasRoleChangeModal: false,
      handleWorkHistoryCompanyChange: this.handleWorkHistoryCompanyChange,
      handleWorkHistoryCompanyBlur: this.handleWorkHistoryCompanyBlur,
      handleWorkHistoryPositionChange: this.handleWorkHistoryPositionChange,
      handleCurrentPositionInputChange: this.handleCurrentPositionInputChange,
      handleWorkHistoryDateInputChange: this.handleWorkHistoryDateInputChange,
      handleWorkHistoryInputChange: this.handleWorkHistoryInputChange,
      handleWorkHistoryDelete: this.handleWorkHistoryDelete,
      handleToggleEditingWork: this.handleToggleEditingWork,
      handleCancelEditingWork: this.handleCancelEditingWork,
      pickerOptions,
    };

    const displayWorkProps = {
      setWHRef: (ref) => this.setWHRef(ref, idx),
      workHistory,
      idx,
      handleToggleEditingWork: this.handleToggleEditingWork,
      workDisplayType: 'limited',
    };

    return isEditing ? (
      <WorkInputs key={workHistory.id || idx} {...workInputsProps} />
    ) : (
      <WorkDisplay key={workHistory.id || idx} {...displayWorkProps} />
    );
  };

  handleWorkHistoryCompanyChange = ({ event, idx: workHistoryIdx }) => {
    const {
      target: { value: newValue },
    } = event;

    const { workHistories } = this.state;

    const newWorkHistories = workHistories.slice();

    newWorkHistories[workHistoryIdx].company = {
      ...newWorkHistories[workHistoryIdx].company,
      name: newValue.name,
      id: newValue.id,
    };

    newWorkHistories[workHistoryIdx].companyId = newValue.id;

    this.setState({ workHistories: newWorkHistories });
  };

  handleWorkHistoryCompanyBlur = ({ event, idx: workHistoryIdx }) => {
    const {
      target: { value: newValue },
    } = event;

    const { workHistories } = this.state;

    const { company: origCompany = {} } = workHistories[workHistoryIdx];

    if (origCompany.name !== newValue) {
      this.setState({ isPostingCompany: true });

      const onFulfilled = ({ company }) => {
        const newWorkHistories = workHistories.slice();

        newWorkHistories[workHistoryIdx].company = {
          ...newWorkHistories[workHistoryIdx].company,
          name: company.name,
          id: company.id,
        };

        newWorkHistories[workHistoryIdx].companyId = company.id;

        this.setState({ workHistories: newWorkHistories, isPostingCompany: false });
      };

      const onFail = (error) => {
        handleError(error);
        this.setState({ isPostingCompany: false });
      };

      const data = {
        type: 'company',
        attributes: {
          name: newValue.trim(),
        },
      };

      promisePostCompanies({ data }).then(onFulfilled).catch(onFail);
    }
  };

  handleCurrentPositionInputChange = ({ event, idx: workHistoryIdx }) => {
    const {
      target: { checked },
    } = event;

    const { workHistories } = this.state;

    const newWorkHistories = workHistories.map((wh, idx) => {
      if (idx === workHistoryIdx) {
        return {
          ...wh,
          endDate: checked ? null : new Date().toISOString().substring(0, 10),
        };
      }
      return wh;
    });

    this.setState({ workHistories: newWorkHistories });
  };

  handleWorkHistoryDateInputChange = ({ event, idx: workHistoryIdx }) => {
    const {
      target: { name, value: newValue },
    } = event;

    const { workHistories } = this.state;

    const { startDate, startMonth, startYear, endMonth, endYear, endDate } =
      workHistories[workHistoryIdx];

    const newWorkHistory = {
      startDate,
      startMonth,
      startYear,
      endMonth,
      endYear,
      endDate,
    };

    const startDateObject = startDate ? dayjs(startDate, 'YYYY-MM-DD').toDate() : null;
    const endDateObject = endDate ? dayjs(endDate, 'YYYY-MM-DD').toDate() : null;

    if (name === 'startMonth') {
      if (startDateObject) {
        startDateObject.setMonth(newValue - 1, 1);
        newWorkHistory.startDate = startDateObject.toISOString().substring(0, 10);
      } else if (startYear !== '') {
        const newStartDateObject = new Date(startYear, newValue - 1, 1);
        newWorkHistory.startDate = newStartDateObject.toISOString().substring(0, 10);
      } else {
        newWorkHistory.startMonth = newValue;
      }
    }

    if (name === 'startYear') {
      if (startDateObject) {
        startDateObject.setFullYear(newValue);
        newWorkHistory.startDate = startDateObject.toISOString().substring(0, 10);
      } else if (startMonth !== '') {
        const newStartDateObject = new Date(newValue, startMonth - 1, 1);
        newWorkHistory.startDate = newStartDateObject.toISOString().substring(0, 10);
      } else {
        newWorkHistory.startYear = newValue;
      }
    }

    if (name === 'endMonth') {
      if (endDateObject) {
        endDateObject.setMonth(newValue - 1, 1);
        newWorkHistory.endDate = endDateObject.toISOString().substring(0, 10);
      } else if (endYear !== '') {
        const newEndDateObject = new Date(endYear, newValue - 1, 1);
        newWorkHistory.endDate = newEndDateObject.toISOString().substring(0, 10);
      } else {
        newWorkHistory.endMonth = newValue;
      }
    }

    if (name === 'endYear') {
      if (endDateObject) {
        endDateObject.setFullYear(newValue);
        newWorkHistory.endDate = endDateObject.toISOString().substring(0, 10);
      } else if (endMonth !== '') {
        const newEndDateObject = new Date(newValue, endMonth - 1, 1);
        newWorkHistory.endDate = newEndDateObject.toISOString().substring(0, 10);
      } else {
        newWorkHistory.endYear = newValue;
      }
    }

    const newWorkHistories = workHistories.map((wh, idx) => {
      if (idx === workHistoryIdx) {
        return {
          ...wh,
          ...newWorkHistory,
        };
      }
      return wh;
    });

    this.setState({ workHistories: newWorkHistories });
  };

  handleWorkHistoryInputChange = ({ event, idx: workHistoryIdx }) => {
    const {
      target: { name, value: newValue },
    } = event;

    const { workHistories } = this.state;

    const newWorkHistories = workHistories.map((wh, idx) => {
      if (idx === workHistoryIdx) {
        return {
          ...wh,
          [name]: newValue,
        };
      }
      return wh;
    });

    this.setState({ workHistories: newWorkHistories });
  };

  handleWorkHistoryDelete = ({ idx }) => {
    const {
      actions: { workHistoryDeleteStarted },
    } = this.props;

    const { workHistories } = this.state;

    const newWorkHistories = workHistories.slice();
    newWorkHistories.splice(idx, 1);

    this.setState({ workHistories: newWorkHistories });
    if (workHistories[idx].id) {
      workHistoryDeleteStarted({ workHistoryId: workHistories[idx].id });
    }
  };

  render() {
    const { workHistories, candidateEducations, recentGrad: recentGradTemp } = this.state;

    const {
      actions: { patchCandidateStarted },
      user: { currentProfile },
      serverErrors = [],
      actions: { candidateImageUploadStarted },
    } = this.props;

    const {
      id: candidateId,
      picture: { xlarge: pictureUrl = '' } = {},
      lastName,
      recentGrad: recentGradSaved,
    } = currentProfile || {};

    const recentGrad = recentGradTemp || recentGradSaved;

    const serverErrorMessage = serverErrors.length > 0 ? serverErrors[0] : null;

    const serverErrorContent = serverErrorMessage ? (
      <div className={styles.errorMessage}>{serverErrorMessage}</div>
    ) : null;

    const titleProps = {
      string: 'Last Step',
    };

    const hasWorkHistory = workHistories.length > 0;
    const { role = null } = hasWorkHistory ? workHistories[0] : {};
    const hasWorkHistoryId = hasWorkHistory && workHistories[0].id;

    const hasValidWorkHistory =
      (recentGrad && (!hasWorkHistory || ['Non-Sales', 'B2C / Retail'].includes(role))) ||
      (hasWorkHistory && hasWorkHistoryId && !workHistories.some((wh) => wh.isEditing === true));

    const hasEducation = candidateEducations.length > 0;
    const hasEducationId = hasEducation && candidateEducations[0].id;

    const hasValidEducation =
      hasEducation && hasEducationId && !candidateEducations.some((edu) => edu.isEditing === true);

    const isValid =
      recentGrad ||
      (hasEducation ||
      (recentGrad && (!hasWorkHistory || ['Non-Sales', 'B2C / Retail'].includes(role)))
        ? hasValidWorkHistory && hasValidEducation
        : hasValidWorkHistory);

    const continueOnboardingProps = {
      primary: true,
      className: styles.actionButton,
      disabled: !isValid,
      onClick: () => patchCandidateStarted({ quickOption: 'continue' }),
    };

    const goToForumProps = {
      quaternary: true,
      className: styles.actionButton,
      disabled: !isValid,
      onClick: () => patchCandidateStarted({ quickOption: 'forum' }),
    };

    const mainContentProps = {
      lastName,
      workHistories,
      handleAddWorkHistory: this.handleAddWorkHistory,
      makeWorkHistoryInputs: this.makeWorkHistoryInputs,
    };

    const content = <MainContent {...mainContentProps} />;

    const imageUploaderProps = {
      candidateId,
      pictureUrl,
      candidateImageUploadStarted,
    };

    return [
      <Helmet key="helmet" title="Create Profile" />,

      <ReadyContainer key="CreateProfile" className={styles.CandidateRegistration}>
        <div className={styles.mainContainer}>
          <Title {...titleProps} />

          {serverErrorContent}

          <div className={styles.imageContainer}>
            <CandidateImageUploader {...imageUploaderProps} />
          </div>

          {content}

          <div className={styles.buttonContainer}>
            <Button {...goToForumProps}>Go to forum</Button>

            <Content>or</Content>

            <Button {...continueOnboardingProps}>Finish profile</Button>

            <Content>to get awesome job offers</Content>
          </div>
        </div>
      </ReadyContainer>,
    ];
  }
}

export default ConnectContainer(duck)(CreateProfile);
